import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import EnMultipleAutocompleteFilterButton from 'Components/datagrid/filterButtons/EnMultipleAutocompleteFilterButton.vue';
import EnMultipleSearchFilterButton from 'Components/datagrid/filterButtons/EnMultipleSearchFilterButton.vue';
import Filter from '../types/filter';
import { FilterType } from 'Js/enums/filterType.js';
import { FilterButtonType } from '../types/filterButtonType';
import { addTermToFilter } from 'Js/utils/devExtremeSearchUtils';
import { FilterEvent } from '../types/filterEvent';
import { useResetFilter } from '../composables/useFilterReset';
import { cloneDeep } from 'Js/utils/clone';
/**
 * A filter button specifically for an address.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnAddressSearchFilterButton',
    props: /*@__PURE__*/ _mergeModels({
        /**
         * Unique id of this filter button instance.
         */
        id: String,
        /**
         * Label shown on the filter button.
         */
        label: String,
        /**
         * Hint shown in the filter button.
         */
        hint: String,
        /**
         * The ApiUrl called when isAutocomplete is true.
         */
        apiUrl: String,
        /**
         * Experimental feature to allow search as "Like" in sql
         */
        allowLike: {
            type: Boolean,
            default: false,
        },
        /**
         * Specifies whether an MultipleAutocompleteFilter is used (true) or not (false).
         */
        isAutocomplete: Boolean,
        /**
         * Specifies whether the label filter is added (true) or not (false).
         */
        hasLabel: Boolean,
        /**
         * Specifies whether the position filter is added (true) or not (false).
         */
        hasPosition: Boolean,
        /**
         * Specifies whether the ConsumptionPointType filter is added (true) or not (false).
         */
        hasConsumptionPointType: Boolean,
        /**
         * Specifies whether the house number filters are added (true) or not (false).
         * Defaults to true.
         */
        hasHouseNumber: {
            type: Boolean,
            default: true,
        },
        /**
         * Specifies whether the filter type selection is shown (true) or not (false).
         */
        filterTypeSelection: Boolean,
        /**
         * Overrides dataField options for filters.
         */
        dataFields: {
            type: Object,
            default: {},
        },
        /**
         * Overrides property options for filters.
         */
        properties: Object,
        /**
         * Optional display separator for non-autocomplete address filter value preview.
         */
        displaySeparator: String,
        /**
         * If false, nested filters are emitted as top-level terms instead of one grouped expression.
         */
        combineNestedFilters: {
            type: Boolean,
            default: true,
        },
        /**
         * Adds a full-value autocomplete field above the nested address fields.
         */
        fullValueAutocomplete: {
            type: [Boolean, Object],
            default: false,
        },
    }, {
        "modelValue": {},
        "modelModifiers": {},
        "openOnRender": {},
        "openOnRenderModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels([FilterEvent.Input, 'update:modelValue', FilterEvent.FilterUpdated, FilterEvent.FilterReset], ["update:modelValue", "update:openOnRender"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const props = __props;
        const { t } = useI18n();
        const modelValue = _useModel(__props, 'modelValue');
        const openOnRender = _useModel(__props, 'openOnRender');
        const filters = ref({});
        const filterKey = ref(0);
        const notAutocompleteFilterButton = ref();
        const emit = __emit;
        const { resetFilter } = useResetFilter(emit);
        const filterButtonLabel = computed(() => props.label ?? t('common.address.generic'));
        const filterButtonHint = computed(() => props.hint ?? t('common.address.generic'));
        const filterButtonId = computed(() => props.id ?? 'filter-button-address');
        const fullValueAutocompleteOptions = computed(() => {
            if (!props.fullValueAutocomplete) {
                return null;
            }
            return props.fullValueAutocomplete === true ? {} : props.fullValueAutocomplete;
        });
        const sectionFilterDataField = computed(() => fullValueAutocompleteOptions.value?.sectionFilterDataField);
        function getFilterLabel(key, fallback) {
            return props.properties?.[`${key}Label`] ?? fallback;
        }
        function getFilterInputId(key) {
            return props.properties?.[`${key}InputId`] ?? key;
        }
        function buildSectionFilter(sectionIndex) {
            if (!sectionFilterDataField.value) {
                return undefined;
            }
            return (value) => {
                if (!value) {
                    return undefined;
                }
                const sectionPattern = `%${value}%`;
                const sections = ['%', '%', '%', '%', '%'];
                sections[sectionIndex] = sectionPattern;
                return [sectionFilterDataField.value, FilterType.Like, sections.join('|')];
            };
        }
        function updateFilters(value) {
            emit(FilterEvent.Input, value);
            emit('update:modelValue', value);
            emit(FilterEvent.FilterUpdated, value);
        }
        function consumptionPointFilterExpression({ postalCode, city, street, houseNumber, houseNumberSuffix, label, position, consumptionPointType }) {
            let addressFilter = [];
            const postalCodeFilter = filters.value.postalCode;
            if (!postalCode && postalCodeFilter.value) {
                addTermToFilter(addressFilter, [postalCodeFilter.property, FilterType.Equal, postalCodeFilter.value]);
            }
            else if (postalCode) {
                addTermToFilter(addressFilter, [postalCodeFilter.property, FilterType.StartsWith, postalCode]);
            }
            const cityFilter = filters.value.city;
            if (!city && cityFilter.value) {
                addTermToFilter(addressFilter, [cityFilter.property, FilterType.Equal, cityFilter.value]);
            }
            else if (city) {
                addTermToFilter(addressFilter, [cityFilter.property, FilterType.Contains, city]);
            }
            const streetFilter = filters.value.street;
            if (!street && streetFilter.value) {
                addTermToFilter(addressFilter, [streetFilter.property, FilterType.Equal, streetFilter.value]);
            }
            else if (street) {
                addTermToFilter(addressFilter, [streetFilter.property, FilterType.Contains, street]);
            }
            if (props.hasHouseNumber) {
                const houseNumberFilter = filters.value.houseNumber;
                if (!houseNumber && houseNumberFilter.value) {
                    addTermToFilter(addressFilter, [houseNumberFilter.property, FilterType.Equal, houseNumberFilter.value]);
                }
                else if (houseNumber) {
                    addTermToFilter(addressFilter, [houseNumberFilter.property, FilterType.StartsWith, houseNumber]);
                }
                const houseNumberSuffixFilter = filters.value.houseNumberSuffix;
                if (!houseNumberSuffix && houseNumberSuffixFilter.value) {
                    addTermToFilter(addressFilter, [houseNumberSuffixFilter.property, FilterType.Equal, houseNumberSuffixFilter.value]);
                }
                else if (houseNumberSuffix) {
                    addTermToFilter(addressFilter, [houseNumberSuffixFilter.property, FilterType.Contains, houseNumberSuffix]);
                }
            }
            if (props.hasLabel) {
                const labelFilter = filters.value.label;
                if (!label && labelFilter.value) {
                    addTermToFilter(addressFilter, [labelFilter.property, FilterType.Equal, labelFilter.value]);
                }
                else if (label) {
                    addTermToFilter(addressFilter, [labelFilter.property, FilterType.Contains, label]);
                }
            }
            if (props.hasPosition) {
                const positionFilter = filters.value.position;
                if (!position && positionFilter.value) {
                    addTermToFilter(addressFilter, [positionFilter.property, FilterType.Equal, positionFilter.value]);
                }
                else if (position) {
                    addTermToFilter(addressFilter, [positionFilter.property, FilterType.Contains, position]);
                }
            }
            if (props.hasConsumptionPointType) {
                const consumptionPointTypeFilter = filters.value.consumptionPointType;
                if (!consumptionPointType && consumptionPointTypeFilter.value) {
                    addTermToFilter(addressFilter, [consumptionPointTypeFilter.property, FilterType.Equal, consumptionPointTypeFilter.value]);
                }
                else if (consumptionPointType) {
                    addTermToFilter(addressFilter, [consumptionPointTypeFilter.property, FilterType.Contains, consumptionPointType]);
                }
            }
            return addressFilter;
        }
        function createFiltersForAutocomplete(presetFilter) {
            presetFilter ??= {};
            const defaultStreetField = props.hasHouseNumber ? 'Street' : 'StreetFull';
            const filters = {
                postalCode: new Filter({
                    label: getFilterLabel('postalCode', t('common.address.postalCode')),
                    inputId: getFilterInputId('postalCode'),
                    apiUrl: props.apiUrl,
                    dataField: props.dataFields?.postalCode ?? 'ConsumptionPoint',
                    property: props.properties?.postalCode ?? 'PostalCode',
                    combine: props.combineNestedFilters,
                    searchExpression: (searchTerm) => consumptionPointFilterExpression({ postalCode: searchTerm }),
                    resetValue: null,
                    value: presetFilter.postalCode?.value
                }),
                city: new Filter({
                    label: getFilterLabel('city', t('common.address.city')),
                    inputId: getFilterInputId('city'),
                    apiUrl: props.apiUrl,
                    dataField: props.dataFields?.city ?? 'ConsumptionPoint',
                    property: props.properties?.city ?? 'City',
                    combine: props.combineNestedFilters,
                    searchExpression: (searchTerm) => consumptionPointFilterExpression({ city: searchTerm }),
                    resetValue: null,
                    value: presetFilter.city?.value
                }),
                street: new Filter({
                    label: getFilterLabel('street', props.hasHouseNumber ? t('common.address.street') : t('common.address.streetFull')),
                    inputId: getFilterInputId('street'),
                    apiUrl: props.apiUrl,
                    dataField: props.dataFields?.street ?? 'ConsumptionPoint',
                    property: props.properties?.street ?? defaultStreetField,
                    allowLike: props.allowLike,
                    combine: props.combineNestedFilters,
                    searchExpression: (searchTerm) => consumptionPointFilterExpression({ street: searchTerm }),
                    resetValue: null,
                    value: presetFilter.street?.value
                }),
            };
            if (props.hasHouseNumber) {
                filters.houseNumber = new Filter({
                    label: getFilterLabel('houseNumber', t('common.address.houseNumber')),
                    inputId: getFilterInputId('houseNumber'),
                    apiUrl: props.apiUrl,
                    dataField: props.dataFields?.houseNumber ?? 'ConsumptionPoint',
                    property: props.properties?.houseNumber ?? 'HouseNumber',
                    combine: props.combineNestedFilters,
                    searchExpression: (searchTerm) => consumptionPointFilterExpression({ houseNumber: searchTerm }),
                    resetValue: null,
                    value: presetFilter.houseNumber?.value
                });
                filters.houseNumberSuffix = new Filter({
                    label: getFilterLabel('houseNumberSuffix', t('common.address.houseNumberSuffix')),
                    inputId: getFilterInputId('houseNumberSuffix'),
                    apiUrl: props.apiUrl,
                    dataField: props.dataFields?.houseNumberSuffix ?? 'ConsumptionPoint',
                    property: props.properties?.houseNumberSuffix ?? 'HouseNumberSuffix',
                    combine: props.combineNestedFilters,
                    searchExpression: (searchTerm) => consumptionPointFilterExpression({ houseNumberSuffix: searchTerm }),
                    resetValue: null,
                    value: presetFilter.houseNumberSuffix?.value
                });
            }
            if (props.hasLabel) {
                filters.label = new Filter({
                    label: getFilterLabel('label', t('common.address.label')),
                    inputId: getFilterInputId('label'),
                    apiUrl: props.apiUrl,
                    dataField: props.dataFields?.label ?? 'ConsumptionPoint',
                    property: props.properties?.label ?? 'Label',
                    combine: props.combineNestedFilters,
                    searchExpression: (searchTerm) => consumptionPointFilterExpression({ label: searchTerm }),
                    resetValue: null,
                    value: presetFilter.label?.value
                });
            }
            if (props.hasPosition) {
                filters.position = new Filter({
                    label: getFilterLabel('position', t('common.address.position')),
                    inputId: getFilterInputId('position'),
                    apiUrl: props.apiUrl,
                    dataField: props.dataFields?.position ?? 'ConsumptionPoint',
                    property: props.properties?.position ?? 'Position',
                    combine: props.combineNestedFilters,
                    searchExpression: (searchTerm) => consumptionPointFilterExpression({ position: searchTerm }),
                    resetValue: null,
                    value: presetFilter.position?.value
                });
            }
            if (props.hasConsumptionPointType) {
                filters.consumptionPointType = new Filter({
                    label: getFilterLabel('position', t('common.address.consumptionPointType')),
                    inputId: getFilterInputId('consumptionPointType'),
                    apiUrl: props.apiUrl,
                    dataField: props.dataFields?.consumptionPointType ?? 'ConsumptionPoint',
                    property: props.properties?.consumptionPointType ?? 'ConsumptionPointType',
                    combine: props.combineNestedFilters,
                    searchExpression: (searchTerm) => consumptionPointFilterExpression({ consumptionPointType: searchTerm }),
                    resetValue: null,
                    value: presetFilter.consumptionPointType?.value
                });
            }
            return filters;
        }
        function createFilters(presetFilter) {
            presetFilter ??= {};
            const defaultStreetDataField = props.hasHouseNumber ? 'ConsumptionPoint.Street' : 'ConsumptionPoint.StreetFull';
            const filters = {};
            if (fullValueAutocompleteOptions.value) {
                const autocompleteOptions = fullValueAutocompleteOptions.value;
                const dataField = autocompleteOptions.dataField ?? 'ConsumptionPoint';
                filters.fullValue = new Filter({
                    label: autocompleteOptions.label ?? filterButtonLabel.value,
                    inputId: autocompleteOptions.inputId ?? 'fullValue',
                    apiUrl: autocompleteOptions.apiUrl ?? props.apiUrl,
                    dataField,
                    filterType: presetFilter.fullValue?.filterType ?? FilterType.Equal,
                    filterButtonType: FilterButtonType.Autocomplete,
                    combine: autocompleteOptions.combine ?? props.combineNestedFilters,
                    resetValue: null,
                    value: presetFilter.fullValue?.value,
                    searchExpression: autocompleteOptions.searchExpression ?? dataField,
                    searchValue: autocompleteOptions.searchValue,
                    selectExpression: autocompleteOptions.selectExpression ?? [dataField],
                    itemValue: autocompleteOptions.itemValue ?? dataField,
                    displayExpression: autocompleteOptions.displayExpression ?? dataField,
                    group: autocompleteOptions.group,
                    hideNullValues: autocompleteOptions.hideNullValues ?? true,
                    useContains: autocompleteOptions.useContains ?? false,
                    itemsPerPage: autocompleteOptions.itemsPerPage ?? 10,
                });
            }
            Object.assign(filters, {
                postalCode: new Filter({
                    label: getFilterLabel('postalCode', t('common.address.postalCode')),
                    inputId: getFilterInputId('postalCode'),
                    dataField: props.dataFields?.postalCode ?? 'ConsumptionPoint.PostalCode',
                    filterType: presetFilter.postalCode?.filterType ?? FilterType.Contains,
                    combine: props.combineNestedFilters,
                    resetValue: null,
                    value: presetFilter.postalCode?.value
                }),
                city: new Filter({
                    label: getFilterLabel('city', t('common.address.city')),
                    inputId: getFilterInputId('city'),
                    dataField: props.dataFields?.city ?? 'ConsumptionPoint.City',
                    filterType: presetFilter.city?.filterType ?? FilterType.Contains,
                    combine: props.combineNestedFilters,
                    resetValue: null,
                    value: presetFilter.city?.value
                }),
                street: new Filter({
                    label: getFilterLabel('street', props.hasHouseNumber ? t('common.address.street') : t('common.address.streetFull')),
                    inputId: getFilterInputId('street'),
                    dataField: props.dataFields?.street ?? defaultStreetDataField,
                    filterType: presetFilter.street?.filterType ?? (props.allowLike ? FilterType.Like : FilterType.Contains),
                    combine: props.combineNestedFilters,
                    allowLike: props.allowLike, resetValue: null,
                    value: presetFilter.street?.value
                }),
            });
            if (props.hasHouseNumber) {
                filters.houseNumber = new Filter({
                    label: getFilterLabel('houseNumber', t('common.address.houseNumber')),
                    inputId: getFilterInputId('houseNumber'),
                    dataField: props.dataFields?.houseNumber ?? 'ConsumptionPoint.HouseNumber',
                    filterType: presetFilter.houseNumber?.filterType ?? FilterType.Contains,
                    combine: props.combineNestedFilters,
                    resetValue: null,
                    value: presetFilter.houseNumber?.value
                });
                filters.houseNumberSuffix = new Filter({
                    label: getFilterLabel('houseNumberSuffix', t('common.address.houseNumberSuffix')),
                    inputId: getFilterInputId('houseNumberSuffix'),
                    dataField: props.dataFields?.houseNumberSuffix ?? 'ConsumptionPoint.HouseNumberSuffix',
                    filterType: presetFilter.houseNumberSuffix?.filterType ?? FilterType.Contains,
                    combine: props.combineNestedFilters,
                    resetValue: null,
                    value: presetFilter.houseNumberSuffix?.value
                });
            }
            if (props.hasLabel) {
                filters.label = new Filter({
                    label: getFilterLabel('label', t('common.address.label')),
                    inputId: getFilterInputId('label'),
                    dataField: props.dataFields?.label ?? 'ConsumptionPoint.Label',
                    filterType: presetFilter.label?.filterType ?? FilterType.Contains,
                    combine: props.combineNestedFilters,
                    resetValue: null,
                    value: presetFilter.label?.value
                });
            }
            if (props.hasPosition) {
                filters.position = new Filter({
                    label: getFilterLabel('position', t('common.address.position')),
                    inputId: getFilterInputId('position'),
                    dataField: props.dataFields?.position ?? 'ConsumptionPoint.Position',
                    filterType: presetFilter.position?.filterType ?? FilterType.Contains,
                    combine: props.combineNestedFilters,
                    resetValue: null,
                    value: presetFilter.position?.value
                });
            }
            console.log(props.hasConsumptionPointType);
            if (props.hasConsumptionPointType) {
                filters.consumptionPointType = new Filter({
                    label: getFilterLabel('consumptionPointType', t('common.address.consumptionPointType')),
                    inputId: getFilterInputId('consumptionPointType'),
                    dataField: props.dataFields?.consumptionPointType ?? 'ConsumptionPoint.ConsumptionPointType',
                    filterType: presetFilter.consumptionPointType?.filterType ?? FilterType.Contains,
                    combine: props.combineNestedFilters,
                    resetValue: null,
                    value: presetFilter.consumptionPointType?.value
                });
            }
            return filters;
        }
        watch(modelValue, (newValue, oldValue) => {
            if (Object.keys(newValue).length !== 0) {
                filters.value = newValue;
                notAutocompleteFilterButton.value?.updateDisplayString(filters.value);
            }
        });
        onMounted(() => {
            let loadedFromFilter = false;
            // Get value from filter
            if (modelValue.value) {
                Object.keys(modelValue.value).some(key => {
                    // Filter will not contain label
                    if (!modelValue.value[key].label) {
                        loadedFromFilter = true;
                        return true;
                    }
                });
            }
            if (modelValue.value && Object.keys(modelValue.value).length > 0 && !loadedFromFilter) {
                // filter is initially set
                filters.value = cloneDeep(modelValue.value);
                // force ui to update
                filterKey.value++;
            }
            else {
                if (props.isAutocomplete) {
                    filters.value = createFiltersForAutocomplete(modelValue.value);
                }
                else {
                    filters.value = createFilters(modelValue.value);
                }
            }
            emit(FilterEvent.Input, filters.value);
            emit('update:modelValue', filters.value);
        });
        const __returned__ = { props, t, modelValue, openOnRender, filters, filterKey, notAutocompleteFilterButton, emit, resetFilter, filterButtonLabel, filterButtonHint, filterButtonId, fullValueAutocompleteOptions, sectionFilterDataField, getFilterLabel, getFilterInputId, buildSectionFilter, updateFilters, consumptionPointFilterExpression, createFiltersForAutocomplete, createFilters, EnMultipleAutocompleteFilterButton, EnMultipleSearchFilterButton };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
